<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch
} from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'
import {
  pageTutorialSteps,
  pageTabTutorialSteps,
  useTutorialStore
} from '@/stores/tutorial'

const props = defineProps({
  currentPath: {
    type: String,
    default: ''
  }
})

const auth = useAuthStore()
const settings = useSettingsStore()
const tutorial = useTutorialStore()

const card = ref(null)
const heading = ref(null)
const targetRect = ref(null)
const cardPosition = ref({})
let layoutFrame = null
let activatedStepKey = null
let cardResizeObserver = null

const stepNumber = computed(() => tutorial.currentIndex + 1)

function normalizedPath(path) {
  return String(path || '').replace(/\/$/, '') || '/'
}

const spotlightStyle = computed(() => {
  const rect = targetRect.value
  if (!rect) return null

  return {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`
  }
})

function boundedRect(element) {
  if (!element) return null

  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null

  const padding = 8
  const left = Math.max(8, rect.left - padding)
  const top = Math.max(8, rect.top - padding)
  const right = Math.min(window.innerWidth - 8, rect.right + padding)
  const bottom = Math.min(window.innerHeight - 8, rect.bottom + padding)

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
    right,
    bottom
  }
}

function updateLayout() {
  if (!tutorial.isOpen || !tutorial.currentStep) return

  const selector = tutorial.currentStep.target
  const element = selector ? document.querySelector(selector) : null
  const rect = boundedRect(element)
  targetRect.value = rect

  const viewportPadding = 16
  const gap = 16
  const availableWidth = Math.max(0, window.innerWidth - (viewportPadding * 2))
  const availableHeight = Math.max(0, window.innerHeight - (viewportPadding * 2))
  const width = Math.min(420, availableWidth)
  const height = Math.min(card.value?.offsetHeight || 280, availableHeight)

  const clampTop = top => Math.min(
    Math.max(viewportPadding, window.innerHeight - height - viewportPadding),
    Math.max(viewportPadding, top)
  )
  const positionedCard = (left, proposedTop) => {
    const top = clampTop(proposedTop)

    return {
      left: `${left}px`,
      top: `${top}px`,
      maxHeight: `${Math.max(0, window.innerHeight - top - viewportPadding)}px`
    }
  }

  if (!rect) {
    cardPosition.value = positionedCard(
      Math.max(viewportPadding, (window.innerWidth - width) / 2),
      (window.innerHeight - height) / 2
    )
    return
  }

  let top
  if (rect.bottom + gap + height <= window.innerHeight - viewportPadding) {
    top = rect.bottom + gap
  } else if (rect.top - gap - height >= viewportPadding) {
    top = rect.top - gap - height
  } else {
    top = Math.max(viewportPadding, (window.innerHeight - height) / 2)
  }

  const centeredLeft = rect.left + (rect.width / 2) - (width / 2)
  const left = Math.min(
    window.innerWidth - width - viewportPadding,
    Math.max(viewportPadding, centeredLeft)
  )

  cardPosition.value = positionedCard(left, top)
}

async function positionCurrentStep() {
  await nextTick()

  observeTutorialCard()

  const step = tutorial.currentStep
  if (!tutorial.isOpen || !step) return

  const routeChanging = Boolean(
    step.path &&
    normalizedPath(step.path) !== normalizedPath(props.currentPath)
  )
  const stepKey = `${tutorial.currentIndex}:${step.id}`

  if (activatedStepKey !== stepKey) {
    activatedStepKey = stepKey
    closeExpandedTargets(step.closeTargets)

    if (step.openTarget) {
      activateElement(document.querySelector(step.openTarget))
      await nextTick()
    }

    if (step.requireTarget && !document.querySelector(step.requireTarget)) {
      tutorial.steps.splice(tutorial.currentIndex, 1)
      tutorial.currentIndex = Math.min(
        tutorial.currentIndex,
        Math.max(0, tutorial.steps.length - 1)
      )
      activatedStepKey = null

      if (tutorial.steps.length === 0) {
        tutorial.isOpen = false
        return
      }

      await positionCurrentStep()
      return
    }

    const activationTarget = step.activationTarget || (
      step.activateTarget ? step.target : null
    )

    if (
      activationTarget &&
      (step.activateOnCurrentPath || !step.path || routeChanging)
    ) {
      activateElement(document.querySelector(activationTarget))
      await nextTick()
    }

    if (step.optionalActivationTarget) {
      const optionalTarget = document.querySelector(step.optionalActivationTarget)
      if (optionalTarget) {
        activateElement(optionalTarget)
        await nextTick()
      }
    }

    if (step.dispatchTarget && step.dispatchEvent) {
      document.querySelector(step.dispatchTarget)?.dispatchEvent(
        new CustomEvent(step.dispatchEvent)
      )
      await nextTick()
    }

    if (step.inputTarget) {
      setInputValue(
        document.querySelector(step.inputTarget),
        step.inputValue ?? ''
      )
      await nextTick()
    }

    if (step.submitTarget) {
      document.querySelector(step.submitTarget)?.click()
      await nextTick()
    }

    if (step.scrollTargetIntoView) {
      scrollStepTargetIntoView(step.target)
      await nextTick()
    }
  }

  if (!routeChanging) {
    addVisibleTabSteps(step)
    await nextTick()
  }

  if (layoutFrame) cancelAnimationFrame(layoutFrame)
  layoutFrame = requestAnimationFrame(() => {
    updateLayout()
    requestAnimationFrame(() => {
      updateLayout()
      heading.value?.focus()
    })
  })
}

function scrollStepTargetIntoView(selector) {
  const element = selector ? document.querySelector(selector) : null
  if (!element?.scrollIntoView) return

  element.scrollIntoView({
    behavior: 'auto',
    block: 'center',
    inline: 'nearest'
  })
}

function setInputValue(element, value) {
  if (!element) return

  const prototype = Object.getPrototypeOf(element)
  const valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set

  if (valueSetter) {
    valueSetter.call(element, value)
  } else {
    element.value = value
  }

  element.dispatchEvent(new Event('input', { bubbles: true }))
}

function activateElement(element) {
  if (!element) return

  const expanded = element.getAttribute('aria-expanded')
  if (expanded !== null && expanded === 'true') return

  const selected = element.getAttribute('aria-selected')
  if (selected !== null && selected === 'true') return

  element.click()
}

function closeExpandedTargets(selectors = []) {
  for (const selector of selectors) {
    const element = document.querySelector(selector)
    if (element?.getAttribute('aria-expanded') === 'true') {
      element.click()
    }
  }
}

function closeTutorialPanels() {
  closeExpandedTargets([
    '[data-tutorial="notifications-trigger"]',
    '[data-tutorial="accessibility-trigger"]'
  ])

  document.querySelectorAll('[data-tutorial="media-form"]').forEach(element => {
    element.dispatchEvent(new CustomEvent('tutorial:clear-media-example'))
  })
}

function addVisibleTabSteps(step) {
  const isPageStep = step.id?.startsWith('page-') && !step.tabPath
  if (!isPageStep && !step.tabPath) return

  const pageContent = document.querySelector('[data-tutorial="page-content"]')
  if (!pageContent) return

  const visibleTablists = Array.from(
    pageContent.querySelectorAll('[data-tutorial-tablist]')
  ).filter(tablist => isVisibleWithinPage(tablist, pageContent))
  if (visibleTablists.length === 0) return

  const tablists = isPageStep && step.id !== 'page-admin'
    ? shallowestTablists(visibleTablists, pageContent)
    : visibleTablists

  const undiscoveredTablists = tablists.filter(tablist => {
    const groupId = tutorialTabGroupId(tablist)
    return !tutorial.steps.some(existingStep => existingStep.tabGroupId === groupId)
  })
  if (undiscoveredTablists.length === 0) return

  const tabGroups = undiscoveredTablists.map((tablist, groupIndex) =>
    Array.from(tablist.children)
      .filter(element => element.matches('[data-tutorial-tab]'))
      .map(element => ({ element, groupIndex }))
  )
  let tabElements = tabGroups.flat()

  if (step.id === 'page-admin' && tabGroups.length > 1) {
    const [primaryTabs, ...nestedGroups] = tabGroups
    const activeIndex = Math.max(
      0,
      primaryTabs.findIndex(({ element }) => element.getAttribute('aria-selected') === 'true')
    )

    tabElements = [
      ...primaryTabs.slice(0, activeIndex + 1),
      ...nestedGroups.flat(),
      ...primaryTabs.slice(activeIndex + 1)
    ]
  }

  const source = String(step.id || 'page').replace(/[^a-z0-9_-]/gi, '-')
  const tabs = tabElements.map(({ element, groupIndex }, index) => {
    const key = element.dataset.tutorialTab || String(index)
    const targetId = `${source}-${groupIndex}-${index}-${key}`
      .replace(/[^a-z0-9_-]/gi, '-')

    element.dataset.tutorialStepTarget = targetId

    const panel = Array.from(
      pageContent.querySelectorAll('[data-tutorial-panel]')
    ).find(element => element.dataset.tutorialPanel === key)
    const panelTargetId = `${targetId}-panel`
    if (panel) panel.dataset.tutorialStepPanel = panelTargetId

    return {
      key,
      idKey: groupIndex === 0 ? key : `${groupIndex}-${key}`,
      label: element.dataset.tutorialLabel || element.textContent.trim() || 'Section',
      target: `[data-tutorial-step-target="${targetId}"]`,
      panelTarget: panel
        ? `[data-tutorial-step-panel="${panelTargetId}"]`
        : null,
      steps: pageTabTutorialSteps(
        step.id,
        key,
        element.dataset.tutorialLabel || element.textContent.trim() || 'Section'
      ).filter(configuredStep => {
        return (
          !configuredStep.requireTargetImmediately ||
          document.querySelector(configuredStep.requireTarget)
        )
      })
    }
  })

  tutorial.addTabSteps({
    afterStepId: step.id,
    groupId: undiscoveredTablists.map(tutorialTabGroupId).join('::'),
    path: normalizedPath(props.currentPath),
    tabs
  })
}

function shallowestTablists(tablists, pageContent) {
  const depths = tablists.map(tablist => {
    let depth = 0
    let current = tablist.parentElement

    while (current && current !== pageContent) {
      depth += 1
      current = current.parentElement
    }

    return depth
  })
  const minimumDepth = Math.min(...depths)

  return tablists.filter((_, index) => depths[index] === minimumDepth)
}

function tutorialTabGroupId(tablist) {
  const keys = Array.from(tablist.children)
    .filter(element => element.matches('[data-tutorial-tab]'))
    .map(element => element.dataset.tutorialTab)
    .join('|')

  return [
    normalizedPath(props.currentPath),
    tablist.dataset.tutorialTabGroup || keys
  ].join('::')
}

function observeTutorialCard() {
  if (typeof ResizeObserver === 'undefined' || !card.value) return

  cardResizeObserver?.disconnect()
  cardResizeObserver = new ResizeObserver(updateLayout)
  cardResizeObserver.observe(card.value)
}

function isVisibleWithinPage(element, pageContent) {
  let current = element

  while (current && current !== pageContent) {
    if (
      current.hidden ||
      window.getComputedStyle(current).display === 'none'
    ) {
      return false
    }

    current = current.parentElement
  }

  return true
}

function handleWindowChange() {
  if (tutorial.isOpen) updateLayout()
}

function handleKeydown(event) {
  if (!tutorial.isOpen) return

  if (event.key === 'ArrowRight') {
    event.preventDefault()
    tutorial.next()
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault()
    tutorial.back()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    tutorial.skip()
  }
}

watch(
  () => [auth.initialized, auth.user_detail_id, settings.loading, props.currentPath],
  ([initialized, userDetailId, loading, currentPath]) => {
    if (initialized && userDetailId && !loading) {
      const [pageGuide] = pageTutorialSteps(
        currentPath || window.location.pathname
      )

      if (
        tutorial.isOpen &&
        (!tutorial.activeGuideId || tutorial.activeGuideId === pageGuide.id)
      ) {
        positionCurrentStep()
      } else {
        tutorial.maybeStart({
          path: currentPath || window.location.pathname
        })
      }
    }
  },
  { immediate: true }
)

watch(
  () => [tutorial.isOpen, tutorial.currentIndex],
  ([isOpen], [wasOpen] = []) => {
    if (isOpen) {
      positionCurrentStep()
    } else {
      targetRect.value = null
      activatedStepKey = null
      closeTutorialPanels()
      if (wasOpen && !tutorial.startedAutomatically) {
        document.querySelector('[data-tutorial="restart"]')?.focus()
      }
    }
  },
  { flush: 'post', immediate: true }
)

onMounted(() => {
  window.addEventListener('resize', handleWindowChange)
  window.addEventListener('scroll', handleWindowChange, true)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  if (layoutFrame) cancelAnimationFrame(layoutFrame)
  cardResizeObserver?.disconnect()
  window.removeEventListener('resize', handleWindowChange)
  window.removeEventListener('scroll', handleWindowChange, true)
  window.removeEventListener('keydown', handleKeydown)
  tutorial.close()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="tutorial.isOpen && tutorial.currentStep"
      class="tutorial-walkthrough"
      data-testid="tutorial-walkthrough"
    >
      <div class="tutorial-interaction-blocker" aria-hidden="true" />

      <div
        v-if="spotlightStyle"
        class="tutorial-spotlight"
        :style="spotlightStyle"
        aria-hidden="true"
      />

      <div
        v-else
        class="tutorial-backdrop"
        aria-hidden="true"
      />

      <section
        ref="card"
        class="tutorial-card"
        :style="cardPosition"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        aria-describedby="tutorial-description"
      >
        <div class="tutorial-card__content">
          <div class="tutorial-card__progress">
            Step {{ stepNumber }} of {{ tutorial.steps.length }}
          </div>

          <h2
            id="tutorial-title"
            ref="heading"
            class="tutorial-card__title"
            tabindex="-1"
          >
            {{ tutorial.currentStep.title }}
          </h2>

          <p id="tutorial-description" class="tutorial-card__body">
            {{ tutorial.currentStep.body }}
          </p>

          <div class="tutorial-card__dots" aria-hidden="true">
            <span
              v-for="(_, index) in tutorial.steps"
              :key="index"
              class="tutorial-card__dot"
              :class="{ 'tutorial-card__dot--active': index === tutorial.currentIndex }"
            />
          </div>
        </div>

        <div class="tutorial-card__actions">
          <button
            type="button"
            class="button secondary outline"
            @click="tutorial.skip"
          >
            Skip
          </button>

          <div class="tutorial-card__navigation">
            <button
              type="button"
              class="button secondary outline"
              :disabled="tutorial.isFirstStep"
              @click="tutorial.back"
            >
              Back
            </button>

            <button
              type="button"
              class="button primary"
              @click="tutorial.next"
            >
              {{ tutorial.isLastStep ? 'Finish' : 'Next' }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
</template>
